/**
 * Animated demos embedded in each tour tooltip.
 *
 * Each demo is a small React component (~300 × 150px) that visually
 * pantomimes the feature it accompanies. Animations are pure CSS
 * keyframes on a single local <style> block so they don't leak into
 * the rest of the app.
 *
 * Why do this instead of recording GIFs:
 *   - Always matches the live product — no stale screen captures
 *   - Zero bundle weight (no video codec, no hosted assets)
 *   - Easy to tweak later as the UI evolves
 *
 * Style conventions:
 *   - Subtle: the tooltip body is what explains the feature; the demo
 *     just reinforces what to expect visually
 *   - 4 to 6 seconds per loop
 *   - Monospace-ish geometry (fixed widths/heights) so layout doesn't jump
 */

/* ─── Shared frame ─────────────────────────────────────────────────── */

function Frame({ children, id, height = 140 }) {
  return (
    <div
      style={{
        width: '100%',
        height,
        background:
          'linear-gradient(180deg, #fafafa 0%, #f3f4f6 100%)',
        border: '1px solid var(--border)',
        borderRadius: 8,
        overflow: 'hidden',
        position: 'relative',
        fontSize: 11,
        userSelect: 'none',
      }}
      aria-label={`Demo of ${id}`}
    >
      {children}
    </div>
  )
}

/* ─── Shared keyframes ─────────────────────────────────────────────── */

const KEYFRAMES = `
@keyframes td-fade-in { from { opacity: 0; transform: translateY(4px); } to { opacity: 1; transform: none; } }
@keyframes td-type { 0% { width: 0; } 100% { width: 100%; } }
@keyframes td-pulse { 0%, 100% { box-shadow: 0 0 0 0 rgba(59,130,246,0.35); } 50% { box-shadow: 0 0 0 8px rgba(59,130,246,0); } }
@keyframes td-cursor-blink { 0%, 100% { opacity: 1; } 50% { opacity: 0; } }
@keyframes td-cursor-hop { 0% { transform: translate(10px, 60px); } 25% { transform: translate(70px, 60px); } 50% { transform: translate(140px, 60px); } 75% { transform: translate(70px, 60px); } 100% { transform: translate(10px, 60px); } }
@keyframes td-slide-right { from { transform: translateX(-10px); opacity: 0; } to { transform: none; opacity: 1; } }
@keyframes td-cross { from { text-decoration: none; color: var(--text); } to { text-decoration: line-through; color: var(--muted-light); } }
@keyframes td-check { from { background: #fff; } to { background: #22c55e; } }
@keyframes td-bump { 0%, 100% { transform: scale(1); } 50% { transform: scale(1.35); } }
@keyframes td-drop-in { 0% { transform: translateY(-8px) scale(0.85); opacity: 0; } 100% { transform: none; opacity: 1; } }
`

function Keyframes() {
  return <style>{KEYFRAMES}</style>
}

/* ─── 1. Pillar tabs ───────────────────────────────────────────────── */

function PillarsDemo() {
  return (
    <Frame id="pillars">
      <Keyframes />
      <style>{`
        .td-pillars { display: flex; gap: 4px; padding: 16px; flex-wrap: wrap; }
        .td-pill { padding: 5px 10px; border-radius: 20px; background: #fff;
                   border: 1px solid var(--border); font-size: 10px; font-weight: 600;
                   color: var(--text-secondary); }
        .td-pill.active { animation: td-tabs 4s ease-in-out infinite; }
        @keyframes td-tabs {
          0%, 100% { background: #eff6ff; color: #1d4ed8; border-color: #bfdbfe; }
          50% { background: #ecfccb; color: #4d7c0f; border-color: #d9f99d; }
        }
      `}</style>
      <div className="td-pillars">
        <span className="td-pill active">🔄 EO Buyouts</span>
        <span className="td-pill">💰 Revenue</span>
        <span className="td-pill">📈 Growth</span>
        <span className="td-pill">🚀 Delivery</span>
        <span className="td-pill">🤝 Team</span>
        <span className="td-pill">🤖 AI</span>
      </div>
      <div style={{ position: 'absolute', bottom: 10, left: 16, fontSize: 10, color: 'var(--muted)' }}>
        Click any tab to switch.
      </div>
    </Frame>
  )
}

/* ─── 2. Executive Summary ─────────────────────────────────────────── */

function ExecSummaryDemo() {
  return (
    <Frame id="exec-summary">
      <Keyframes />
      <style>{`
        .td-exec { padding: 12px 14px; }
        .td-exec-label { font-size: 9px; font-weight: 800; letter-spacing: 0.05em;
                         text-transform: uppercase; margin-bottom: 3px; }
        .td-exec-line { height: 6px; border-radius: 3px; margin-bottom: 3px; }
        .td-exec-row { margin-bottom: 8px; opacity: 0; animation: td-fade-in 0.5s forwards; }
        .td-exec-row:nth-child(1) { animation-delay: 0.2s; }
        .td-exec-row:nth-child(2) { animation-delay: 1.2s; }
        .td-exec-row:nth-child(3) { animation-delay: 2.2s; }
      `}</style>
      <div className="td-exec">
        <div className="td-exec-row">
          <div className="td-exec-label" style={{ color: '#7c3aed' }}>TLDR</div>
          <div className="td-exec-line" style={{ background: '#ede9fe', width: '100%' }} />
          <div className="td-exec-line" style={{ background: '#ede9fe', width: '85%' }} />
        </div>
        <div className="td-exec-row">
          <div className="td-exec-label" style={{ color: '#166534' }}>Wins</div>
          <div className="td-exec-line" style={{ background: '#dcfce7', width: '90%' }} />
        </div>
        <div className="td-exec-row">
          <div className="td-exec-label" style={{ color: '#b91c1c' }}>Flags</div>
          <div className="td-exec-line" style={{ background: '#fee2e2', width: '70%' }} />
        </div>
      </div>
    </Frame>
  )
}

/* ─── 3. Follow-ups ────────────────────────────────────────────────── */

function FollowUpsDemo() {
  return (
    <Frame id="follow-ups">
      <Keyframes />
      <style>{`
        .td-fu { padding: 12px 14px; display: flex; flex-direction: column; gap: 4px; }
        .td-fu-item { display: flex; align-items: center; gap: 8px; padding: 5px 8px;
                      background: #fafafa; border: 1px solid var(--border); border-radius: 4px; }
        .td-fu-box { width: 12px; height: 12px; border: 1.5px solid #9ca3af;
                     border-radius: 2px; flex-shrink: 0; }
        .td-fu-label { font-size: 10px; flex: 1; color: var(--text); }
        .td-fu-item.done .td-fu-box { animation: td-check 0.4s 2s forwards; border-color: #22c55e; }
        .td-fu-item.done .td-fu-label { animation: td-cross 0.4s 2.1s forwards; }
        .td-fu-overdue { color: #b91c1c; font-size: 9px; font-weight: 700; }
      `}</style>
      <div className="td-fu">
        <div className="td-fu-item done">
          <div className="td-fu-box" />
          <div className="td-fu-label">Finalize pricing page copy</div>
        </div>
        <div className="td-fu-item">
          <div className="td-fu-box" />
          <div className="td-fu-label">Schedule Q3 planning sync</div>
        </div>
        <div className="td-fu-item" style={{ background: '#fef2f2', borderColor: '#fecaca' }}>
          <div className="td-fu-box" />
          <div className="td-fu-label">Review Echo contract</div>
          <span className="td-fu-overdue">⚠ 3d overdue</span>
        </div>
      </div>
    </Frame>
  )
}

/* ─── 4. Archive ───────────────────────────────────────────────────── */

function ArchiveDemo() {
  return (
    <Frame id="archive">
      <Keyframes />
      <style>{`
        .td-arch { padding: 14px; display: flex; align-items: center; gap: 10px; }
        .td-arch-btn { padding: 4px 10px; background: #fff; border: 1px solid var(--border);
                       border-radius: 6px; font-size: 10px; font-weight: 600; color: var(--muted);
                       animation: td-pulse 2.5s infinite; }
        .td-arch-list { display: flex; flex-direction: column; gap: 3px;
                        animation: td-slide-right 0.5s 1s both; }
        .td-arch-week { font-size: 10px; padding: 3px 7px; background: #fff;
                        border: 1px solid var(--border); border-radius: 4px; color: var(--text-secondary); }
        .td-arch-week.current { background: #eff6ff; color: #1d4ed8; border-color: #bfdbfe; }
      `}</style>
      <div className="td-arch">
        <span className="td-arch-btn">📅 Past weeks (3)</span>
        <div className="td-arch-list">
          <span className="td-arch-week current">Apr 14 – Apr 20, current</span>
          <span className="td-arch-week">Apr 7 – Apr 13</span>
          <span className="td-arch-week">Mar 31 – Apr 6</span>
        </div>
      </div>
    </Frame>
  )
}

/* ─── 5. Presence ──────────────────────────────────────────────────── */

function PresenceDemo() {
  return (
    <Frame id="presence">
      <Keyframes />
      <style>{`
        .td-pres { padding: 14px; display: flex; align-items: center; gap: 6px; flex-wrap: wrap; }
        .td-pres-pill { display: inline-flex; align-items: center; gap: 5px;
                        background: #f9fafb; border: 1px solid var(--border); border-radius: 20px;
                        padding: 3px 10px 3px 3px; font-size: 10px; font-weight: 600; color: var(--text-secondary); }
        .td-pres-avatar { width: 18px; height: 18px; border-radius: 50%;
                          color: #fff; font-size: 9px; font-weight: 800;
                          display: inline-flex; align-items: center; justify-content: center;
                          position: relative; }
        .td-pres-avatar::after { content: ''; position: absolute; right: -1px; bottom: -1px;
                                 width: 6px; height: 6px; border-radius: 50%;
                                 background: #22c55e; border: 2px solid #fff; }
        .td-pres-pill.new { animation: td-drop-in 0.4s 1.5s both; }
      `}</style>
      <div className="td-pres">
        <span className="td-pres-pill">
          <span className="td-pres-avatar" style={{ background: '#3b82f6' }}>R</span>Ricky
        </span>
        <span className="td-pres-pill">
          <span className="td-pres-avatar" style={{ background: '#8b5cf6' }}>Z</span>Zoe
        </span>
        <span className="td-pres-pill new">
          <span className="td-pres-avatar" style={{ background: '#22c55e' }}>M</span>Margo
        </span>
      </div>
    </Frame>
  )
}

/* ─── 6. Edit goal (click-to-edit) ─────────────────────────────────── */

function EditGoalDemo() {
  return (
    <Frame id="edit-goal">
      <Keyframes />
      <style>{`
        .td-eg { padding: 16px; }
        .td-eg-row { display: flex; align-items: center; gap: 8px; margin-bottom: 6px; }
        .td-eg-title { font-size: 12px; font-weight: 800; color: var(--text);
                       padding: 3px 6px; border-radius: 4px;
                       animation: td-eg-highlight 4s infinite; }
        @keyframes td-eg-highlight {
          0%, 40% { background: transparent; }
          50%, 90% { background: #dbeafe; box-shadow: 0 0 0 1px #3b82f6 inset; }
          100% { background: transparent; }
        }
        .td-eg-cursor { width: 1px; height: 14px; background: #3b82f6;
                        animation: td-cursor-blink 1s infinite; opacity: 0; }
        .td-eg-title.editing + .td-eg-cursor { animation-delay: 0.6s; opacity: 1; }
        .td-eg-bar { height: 6px; background: #e5e7eb; border-radius: 3px; margin-top: 8px; }
        .td-eg-bar span { display: block; height: 100%; background: var(--accent); border-radius: 3px; width: 60%; }
      `}</style>
      <div className="td-eg">
        <div className="td-eg-row">
          <span className="td-eg-title editing">Hit $200k MRR by EOQ</span>
          <span className="td-eg-cursor" />
        </div>
        <div style={{ fontSize: 10, color: 'var(--muted)' }}>Click to edit</div>
        <div className="td-eg-bar"><span /></div>
      </div>
    </Frame>
  )
}

/* ─── 7. DRI + Support ─────────────────────────────────────────────── */

function DriSupportDemo() {
  return (
    <Frame id="dri-support">
      <Keyframes />
      <style>{`
        .td-ds { padding: 16px; display: flex; flex-wrap: wrap; gap: 6px; align-items: center; }
        .td-ds-pill { display: inline-flex; align-items: center; gap: 4px;
                      border-radius: 12px; padding: 2px 8px; font-size: 10px; font-weight: 700;
                      white-space: nowrap; }
        .td-ds-label { opacity: 0.7; font-weight: 500; }
        .td-ds-plus { background: #fff; color: var(--muted); border: 1px dashed #bfdbfe;
                      border-radius: 12px; padding: 2px 8px; font-size: 10px; font-weight: 700;
                      cursor: pointer; }
        .td-ds-new { animation: td-drop-in 0.4s 2s both; }
      `}</style>
      <div className="td-ds">
        <span className="td-ds-pill" style={{ background: '#fee2e2', color: '#991b1b', border: '1px solid #fecaca' }}>
          <span className="td-ds-label">DRI:</span>Christina
        </span>
        <span className="td-ds-pill" style={{ background: '#ede9fe', color: '#6d28d9', border: '1px solid #ddd6fe' }}>
          <span className="td-ds-label">Support:</span>Zoe ×
        </span>
        <span className="td-ds-pill td-ds-new" style={{ background: '#fff7ed', color: '#ea580c', border: '1px solid #fed7aa' }}>
          Katie ×
        </span>
        <span className="td-ds-plus">＋</span>
      </div>
    </Frame>
  )
}

/* ─── 8. Weekly Summary (three blocks filling in) ──────────────────── */

function WeeklySummaryDemo() {
  return (
    <Frame id="weekly-summary">
      <Keyframes />
      <style>{`
        .td-ws { padding: 10px 12px; display: flex; flex-direction: column; gap: 5px; }
        .td-ws-block { border-radius: 4px; padding: 4px 7px; border: 1px solid;
                       font-size: 9px; font-weight: 700; opacity: 0;
                       animation: td-fade-in 0.4s forwards; }
        .td-ws-block:nth-child(1) { animation-delay: 0.3s; background: #faf5ff;
                                    border-color: #e9d5ff; color: #7c3aed; }
        .td-ws-block:nth-child(2) { animation-delay: 1.2s; background: #f0fdf4;
                                    border-color: #bbf7d0; color: #166534; }
        .td-ws-block:nth-child(3) { animation-delay: 2.1s; background: #fefce8;
                                    border-color: #fde68a; color: #92400e; }
        .td-ws-text { display: block; height: 4px; margin-top: 4px; border-radius: 2px;
                      background: currentColor; opacity: 0.25; }
      `}</style>
      <div className="td-ws">
        <div className="td-ws-block">
          🟣 TLDR
          <span className="td-ws-text" style={{ width: '95%' }} />
          <span className="td-ws-text" style={{ width: '70%' }} />
        </div>
        <div className="td-ws-block">
          🟢 WINS
          <span className="td-ws-text" style={{ width: '85%' }} />
        </div>
        <div className="td-ws-block">
          🟡 FLAGS
          <span className="td-ws-text" style={{ width: '75%' }} />
        </div>
      </div>
    </Frame>
  )
}

/* ─── 9. Weekly Summary — What good looks like (static mockup) ─────── */

function WeeklyExamplesDemo() {
  return (
    <Frame id="weekly-examples" height={260}>
      <style>{`
        .td-we { padding: 8px 10px; display: flex; flex-direction: column; gap: 4px;
                 font-size: 10px; line-height: 1.45; }
        .td-we-row { padding: 5px 8px; border-radius: 4px; border: 1px solid; }
        .td-we-row strong { display: block; font-size: 9px; letter-spacing: 0.04em;
                            text-transform: uppercase; margin-bottom: 2px; }
      `}</style>
      <div className="td-we">
        <div
          className="td-we-row"
          style={{ background: '#faf5ff', borderColor: '#e9d5ff', color: '#5b21b6' }}
        >
          <strong>TLDR</strong>
          MRR held at $98.5k this week — Acme and Beta Corp closed for
          $10k combined ARR, offset by Delta downgrading one seat. Three
          late-stage deals ($22k combined) are targeting EOQ close; if
          two land we clear the $120k target. Pricing page redesign
          shipped Tuesday and is already driving more qualified demos
          from organic traffic.
        </div>
        <div
          className="td-we-row"
          style={{ background: '#f0fdf4', borderColor: '#bbf7d0', color: '#166534' }}
        >
          <strong>Wins</strong>
          Acme + Beta closed. Pricing page shipped. First referral
          partner (Harold's network) delivered a warm intro.
        </div>
        <div
          className="td-we-row"
          style={{ background: '#fefce8', borderColor: '#fde68a', color: '#92400e' }}
        >
          <strong>Flags</strong>
          Echo slipping to Q3 — legal blocking on IP clauses. Need
          Jonathan on contracts by Thursday to recover.
        </div>
      </div>
    </Frame>
  )
}

/* ─── 10. Action items ─────────────────────────────────────────────── */

function ActionItemsDemo() {
  return (
    <Frame id="action-items" height={210}>
      <Keyframes />
      <style>{`
        .td-ai { padding: 10px 12px; display: flex; flex-direction: column; gap: 6px; }
        .td-ai-hdr { font-size: 9px; font-weight: 800; color: var(--muted);
                     text-transform: uppercase; letter-spacing: 0.06em;
                     display: flex; align-items: center; gap: 4px; }
        .td-ai-form { display: flex; align-items: center; gap: 5px; padding: 5px 7px;
                      background: #fff; border: 1px solid var(--border); border-radius: 6px;
                      font-size: 10px; color: var(--text); }
        .td-ai-typed { flex: 1; overflow: hidden; white-space: nowrap;
                       animation: td-type 2.2s steps(32, end) forwards; width: 0; }
        .td-ai-cursor { width: 1px; height: 11px; background: #3b82f6;
                        animation: td-cursor-blink 1s infinite; }
        .td-ai-chip { font-size: 9px; font-weight: 700; padding: 2px 7px;
                      border-radius: 3px; white-space: nowrap;
                      display: inline-flex; align-items: center; gap: 2px;
                      opacity: 0; animation: td-fade-in 0.3s forwards; }
        .td-ai-chip.owner { color: #fff; background: #f97316;
                            animation-delay: 2.4s; }
        .td-ai-chip.due { color: var(--muted); background: #fff;
                          border: 1px solid var(--border);
                          animation-delay: 2.9s; }
        .td-ai-add { font-size: 9px; font-weight: 800; color: #fff;
                     background: var(--accent); padding: 3px 10px; border-radius: 4px;
                     margin-left: auto; opacity: 0;
                     animation: td-fade-in 0.3s 3.4s forwards,
                                td-pulse 1.4s 3.6s infinite; }
        .td-ai-divider { height: 1px; background: var(--border); margin: 2px 0 0; }
        .td-ai-item { display: flex; align-items: center; gap: 6px; padding: 5px 8px;
                      background: #fff; border: 1px solid var(--border); border-radius: 4px;
                      font-size: 10px; opacity: 0;
                      animation: td-drop-in 0.4s 3.9s both; }
        .td-ai-box { width: 11px; height: 11px; border: 1.5px solid #9ca3af;
                     border-radius: 2px; flex-shrink: 0; }
        .td-ai-owner-pill { font-size: 9px; font-weight: 700; padding: 1px 5px;
                            border-radius: 3px; color: #fff; background: #f97316;
                            margin-left: auto; }
        .td-ai-due-pill { font-size: 9px; font-weight: 700; color: var(--muted); }
      `}</style>
      <div className="td-ai">
        <div className="td-ai-hdr">⚡ Add action item</div>

        {/* The new-item form — task, owner, due, Add */}
        <div className="td-ai-form">
          <span className="td-ai-typed">Review Echo contract by EOD Thurs</span>
          <span className="td-ai-cursor" />
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 5 }}>
          <span className="td-ai-chip owner">👤 Katie ▾</span>
          <span className="td-ai-chip due">📅 Apr 24</span>
          <span className="td-ai-add">Add</span>
        </div>

        <div className="td-ai-divider" />

        {/* Result — the new item lands in the list */}
        <div className="td-ai-item">
          <div className="td-ai-box" />
          <span>Review Echo contract by EOD Thurs</span>
          <span className="td-ai-owner-pill">Katie</span>
          <span className="td-ai-due-pill">Apr 24</span>
        </div>
      </div>
    </Frame>
  )
}

/* ─── 11. Comments + @mentions ─────────────────────────────────────── */

function CommentsDemo() {
  return (
    <Frame id="comments">
      <Keyframes />
      <style>{`
        .td-cm { padding: 10px 12px; }
        .td-cm-input { background: #fff; border: 1px solid var(--border); border-radius: 6px;
                       padding: 6px 8px; font-size: 10px; color: var(--text); min-height: 26px; }
        .td-cm-mention { background: #dbeafe; color: #1e40af;
                         padding: 1px 5px; border-radius: 3px; font-weight: 700; font-size: 9px; }
        .td-cm-pop { margin-top: 4px; background: #fff; border: 1px solid var(--border);
                     border-radius: 6px; padding: 4px; box-shadow: 0 4px 12px rgba(0,0,0,0.08);
                     width: 140px; animation: td-drop-in 0.3s 0.8s both; }
        .td-cm-opt { display: flex; align-items: center; gap: 5px;
                     padding: 3px 5px; font-size: 10px; border-radius: 3px; }
        .td-cm-opt.sel { background: #eff6ff; font-weight: 700; }
        .td-cm-avatar { width: 16px; height: 16px; border-radius: 50%;
                        font-size: 9px; font-weight: 800; color: #fff; line-height: 16px; text-align: center; }
      `}</style>
      <div className="td-cm">
        <div className="td-cm-input">
          Good catch — <span className="td-cm-mention">@Zoe</span> can you weigh in?
        </div>
        <div className="td-cm-pop">
          <div className="td-cm-opt sel">
            <span className="td-cm-avatar" style={{ background: '#8b5cf6' }}>Z</span>Zoe
          </div>
          <div className="td-cm-opt">
            <span className="td-cm-avatar" style={{ background: '#f97316' }}>K</span>Katie
          </div>
        </div>
      </div>
    </Frame>
  )
}

/* ─── 12. Reactions ────────────────────────────────────────────────── */

function ReactionsDemo() {
  return (
    <Frame id="reactions">
      <Keyframes />
      <style>{`
        .td-rx { padding: 16px; display: flex; align-items: center; gap: 5px; flex-wrap: wrap; }
        .td-rx-pill { display: inline-flex; align-items: center; gap: 3px;
                      background: #dbeafe; color: #1e40af; border: 1px solid #93c5fd;
                      border-radius: 12px; padding: 1px 7px; font-size: 10px; font-weight: 700; }
        .td-rx-emoji { font-size: 12px; animation: td-bump 1.2s 1s; }
        .td-rx-count { font-variant-numeric: tabular-nums; }
        .td-rx-pick { background: #fff; border: 1px solid var(--border); border-radius: 6px;
                      padding: 5px; display: flex; gap: 3px; box-shadow: 0 4px 12px rgba(0,0,0,0.08);
                      animation: td-drop-in 0.3s 0.5s both; }
        .td-rx-pick span { font-size: 14px; padding: 2px; border-radius: 3px; cursor: pointer; }
        .td-rx-pick span.hot { background: #eff6ff; }
      `}</style>
      <div className="td-rx">
        <span className="td-rx-pill">
          <span className="td-rx-emoji">🎉</span>
          <span className="td-rx-count">3</span>
        </span>
        <span className="td-rx-pill" style={{ background: '#f3f4f6', color: 'var(--text-secondary)', borderColor: '#e5e7eb' }}>
          👍 2
        </span>
        <div className="td-rx-pick">
          <span>🔥</span>
          <span className="hot">🚀</span>
          <span>💯</span>
          <span>✨</span>
        </div>
      </div>
    </Frame>
  )
}

/* ─── 13. Group DM ─────────────────────────────────────────────────── */

function GroupDmDemo() {
  return (
    <Frame id="group-dm" height={240}>
      <Keyframes />
      <style>{`
        .td-gdm { padding: 10px 12px; display: flex; flex-direction: column; gap: 6px; height: 100%; box-sizing: border-box; }
        .td-gdm-hdr { display: flex; align-items: center; gap: 8px;
                      padding-bottom: 6px; border-bottom: 1px solid var(--border); }
        .td-gdm-stack { position: relative; display: inline-flex; }
        .td-gdm-av { width: 18px; height: 18px; border-radius: 50%;
                     color: #fff; font-size: 9px; font-weight: 800;
                     border: 2px solid #fff; display: inline-flex;
                     align-items: center; justify-content: center;
                     margin-left: -7px; }
        .td-gdm-av:first-child { margin-left: 0; }
        .td-gdm-title { font-size: 11px; font-weight: 700; color: var(--text); }
        .td-gdm-sub { font-size: 9px; color: var(--muted); margin-top: 1px; }
        .td-gdm-msgs { flex: 1; display: flex; flex-direction: column; gap: 4px;
                       justify-content: flex-end; overflow: hidden; }
        .td-gdm-msg { display: flex; gap: 6px; align-items: flex-end;
                      opacity: 0; animation: td-fade-in 0.5s forwards; }
        .td-gdm-msg.mine { flex-direction: row-reverse; }
        .td-gdm-msg:nth-child(1) { animation-delay: 0.5s; }
        .td-gdm-msg:nth-child(2) { animation-delay: 2.4s; }
        .td-gdm-msg:nth-child(3) { animation-delay: 4.5s; }
        .td-gdm-msg:nth-child(4) { animation-delay: 6.5s; }
        .td-gdm-avatar { width: 16px; height: 16px; border-radius: 50%;
                         color: #fff; font-size: 8px; font-weight: 800;
                         display: inline-flex; align-items: center; justify-content: center;
                         flex-shrink: 0; }
        .td-gdm-bubble { padding: 5px 8px; border-radius: 10px; font-size: 10px;
                         line-height: 1.35; max-width: 75%;
                         background: #f3f4f6; color: var(--text-secondary); }
        .td-gdm-msg.mine .td-gdm-bubble { background: #3b82f6; color: #fff; }
      `}</style>
      <div className="td-gdm">
        <div className="td-gdm-hdr">
          <span className="td-gdm-stack">
            <span className="td-gdm-av" style={{ background: '#8b5cf6' }}>Z</span>
            <span className="td-gdm-av" style={{ background: '#6366f1' }}>C</span>
            <span className="td-gdm-av" style={{ background: '#3b82f6' }}>R</span>
            <span
              className="td-gdm-av"
              style={{
                background: '#f3f4f6',
                color: 'var(--muted)',
                fontSize: 8,
                fontWeight: 700,
              }}
            >
              +13
            </span>
          </span>
          <div>
            <div className="td-gdm-title">Everyone at Common Trust</div>
            <div className="td-gdm-sub">Group of 16</div>
          </div>
        </div>
        <div className="td-gdm-msgs">
          <div className="td-gdm-msg">
            <span className="td-gdm-avatar" style={{ background: '#8b5cf6' }}>Z</span>
            <span className="td-gdm-bubble">Anyone want to get tacos for lunch? 🌮</span>
          </div>
          <div className="td-gdm-msg">
            <span className="td-gdm-avatar" style={{ background: '#6366f1' }}>C</span>
            <span className="td-gdm-bubble">
              Zoe, you've had tacos for the last 6 months straight! 😂
            </span>
          </div>
          <div className="td-gdm-msg mine">
            <span className="td-gdm-avatar" style={{ background: '#3b82f6' }}>R</span>
            <span className="td-gdm-bubble">
              Let's go to Uno Dos Tacos! 🎉
            </span>
          </div>
        </div>
      </div>
    </Frame>
  )
}

/* ─── Registry ─────────────────────────────────────────────────────── */

export const TOUR_DEMOS = {
  pillars: PillarsDemo,
  'exec-summary': ExecSummaryDemo,
  'follow-ups': FollowUpsDemo,
  archive: ArchiveDemo,
  presence: PresenceDemo,
  'group-dm': GroupDmDemo,
  'edit-goal': EditGoalDemo,
  'dri-support': DriSupportDemo,
  'weekly-summary': WeeklySummaryDemo,
  'weekly-examples': WeeklyExamplesDemo,
  'action-items': ActionItemsDemo,
  comments: CommentsDemo,
  reactions: ReactionsDemo,
}
